import {
  EAnalyticsEvent,
  EAnalyticsLogoutReason,
} from "@frontend-shared/product-analytics";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { authApi } from "@/api/services";
import { PATHS } from "@/constants/paths";
import { useMediaDownloadReset } from "@/hooks/use-media-download";
import { useProductAnalytics } from "@/hooks/use-product-analytics";
import { clearLocalStorage } from "@/lib/utils";

export const useLogout = () => {
  const navigate = useNavigate();

  const { capture } = useProductAnalytics();

  const queryClient = useQueryClient();

  const clearDownloads = useMediaDownloadReset();

  return useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      // NOTE: NSAI-4615 — captured first, while the person is still
      // identified. `ProductAnalyticsProvider` calls reset() once the user
      // query clears, and an event emitted after that would be attributed to
      // a fresh anonymous id instead of the person who signed out.
      capture({
        event: EAnalyticsEvent.AUTH_LOGOUT_COMPLETED,
        properties: { reason: EAnalyticsLogoutReason.USER_INITIATED },
      });

      fetch("/api/docs/session/delete", { method: "POST" }).catch(() => {});

      clearLocalStorage();

      queryClient.clear();

      // NOTE: Abort and drop any in-flight downloads at the logout boundary so a
      // signed-out session can't leak the previous user's meeting title/progress
      // or complete their already-authorized download (CWE-200). This is the
      // guaranteed clear; MediaDownloadAuthSync also covers identity changes that
      // don't go through this hook (cross-tab logout, token expiry).
      clearDownloads();

      navigate(PATHS.AUTH.SIGN_IN);
    },
  });
};
